import React, { useContext, useEffect, useState } from "react";

import IProcessed_Report from "@models/IProcessed_Report";
import IDownloadReportExcelDTO from "@models/DTO/IDownloadReportExcelDTO";
import IOnDownloadReportExcelDTO from "@models/DTO/IOnDownloadReportExcelDTO";
import EndpointsEnum from "@models/EndpointsEnum";

import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwButton from "@components/shared/OwButton/OwButton";
import { ControlBar } from "@components/shared/ControlBar/ControlBar";

import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import { SocketContext } from "@contexts/SocketContext";

type Props = {
    processedReport: IProcessed_Report;
};

export const UnanetFixedPriceExportReport: React.FC<Props> = ({ processedReport }) => {
    const socket = useContext(SocketContext)!;
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const [downloadingCsv, setDownloadingCsv] = useState(false);

    const reportOptions = processedReport.report_options as {
        startDate?: string;
        endDate?: string;
    };

    const downloadFromBase64 = (base64: string, filename: string) => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const onDownloadExcel = (data: IOnDownloadReportExcelDTO) => {
        setDownloadingCsv(false);
        if (data.downloadDirect && data.buffer) {
            downloadFromBase64(data.buffer, data.filename);
        } else if (data.s3Url) {
            window.open(data.s3Url, "_blank");
        }
    };

    const handleDownloadCsv = () => {
        setDownloadingCsv(true);
        const payload: IDownloadReportExcelDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            reportGuid: processedReport.guid,
        };
        socket.emit(EndpointsEnum.ReportDownloadExcel, payload);
    };

    useEffect(() => {
        socket.on(EndpointsEnum.ReportOnDownloadExcel, onDownloadExcel);
        return () => {
            socket.removeListener(EndpointsEnum.ReportOnDownloadExcel, onDownloadExcel);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="UnanetFixedPriceExportReport">
            <OwTitle title={"unanet_fixed_price_export_report"} tag={"h3"} />

            <div className="row mb-3">
                <div className="col-md-8">
                    <ControlBar
                        name={"Unanet Fixed Price Item Export"}
                        sections={[
                            {
                                name: "Download",
                                elements: [
                                    <OwButton
                                        key="download-btn"
                                        title={downloadingCsv ? "generating_csv" : "download_csv"}
                                        leftIconClass={downloadingCsv ? "fas fa-spinner fa-spin" : "fas fa-download"}
                                        fn={handleDownloadCsv}
                                        disabled={downloadingCsv}
                                    />,
                                ],
                            },
                        ]}
                    />
                </div>
            </div>

            {(reportOptions.startDate || reportOptions.endDate) && (
                <div className="mb-3">
                    {reportOptions.startDate && (
                        <span className="me-4">
                            <OwLabel title={"start_date"} isBold={true} className="me-1" />
                            {reportOptions.startDate}
                        </span>
                    )}
                    {reportOptions.endDate && (
                        <span>
                            <OwLabel title={"end_date"} isBold={true} className="me-1" />
                            {reportOptions.endDate}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};
